import React from "react";
import { Play } from "lucide-react";
import { SectionLabel, Reveal } from "./ui";
import { projectPoster, youtubeThumb } from "../../lib/api";

const VideoCard = ({ project, ratio, onPlay, testid }) => {
  const playable = !!project?.video_url;
  const poster = projectPoster(project);
  return (
    <Reveal>
      <div
        onClick={() => playable && onPlay(project)}
        role={playable ? "button" : undefined}
        className="group relative w-full overflow-hidden rounded-xl border transition-all duration-500"
        style={{
          borderColor: "var(--border-color)",
          background: "var(--surface-color)",
          aspectRatio: ratio,
          cursor: playable ? "pointer" : "default",
        }}
        data-testid={testid}
      >
        {poster.src ? (
          <img
            src={poster.src}
            alt=""
            loading="lazy"
            onError={(e) => {
              // maxres thumbnails can 404 for some videos — fall back to hqdefault.
              const t = e.currentTarget;
              if (poster.fromYouTube && !t.dataset.fb) {
                t.dataset.fb = "1";
                t.src = youtubeThumb(project.video_url, "hqdefault");
              }
            }}
            className="absolute inset-0 h-full w-full object-cover transition-all duration-500 group-hover:scale-[1.03] group-hover:brightness-110"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: "var(--surface-color)" }} />
        )}

        {/* small centered play icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="flex h-10 w-10 items-center justify-center rounded-full border backdrop-blur-sm transition-all duration-500 group-hover:scale-110"
            style={{
              borderColor: "rgba(255,255,255,0.35)",
              background: "rgba(0,0,0,0.3)",
              opacity: poster.src ? 0.9 : 0.35,
            }}
          >
            <Play size={15} className="ml-0.5 text-white" fill="white" />
          </span>
        </div>
      </div>
    </Reveal>
  );
};

export const SelectedWork = ({ projects, onPlay }) => {
  const shorts = projects.filter((p) => p.format === "short");
  const longs = projects.filter((p) => p.format === "long");

  return (
    <section id="work" className="relative py-24 md:py-32 px-6 md:px-10">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-16 md:mb-24">
          <SectionLabel>Selected Work</SectionLabel>
          <Reveal>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
              Work that speaks<br />for itself.
            </h2>
          </Reveal>
        </div>

        {/* Short form */}
        <div className="mb-10">
          <SectionLabel>Short Form Videos</SectionLabel>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6" data-testid="short-form-grid">
          {shorts.map((p, i) => (
            <VideoCard key={p.id} project={p} ratio="9 / 16" onPlay={onPlay} testid={`short-card-${i}`} />
          ))}
        </div>

        {/* Long form */}
        <div className="mt-24 md:mt-32 mb-10">
          <SectionLabel>Long Form Videos</SectionLabel>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="long-form-grid">
          {longs.map((p, i) => (
            <VideoCard key={p.id} project={p} ratio="16 / 9" onPlay={onPlay} testid={`long-card-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
};
